import {
  type CanvasElementFrom,
  type PropsOf,
  type WithProps,
  type WithSlots,
} from '@blaze-runtime/engine-domain';
import {
  type ElementSizingMode,
  type PropertiesConfig,
  type PropertiesConfigInputBackground,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';

import { type CanvasElementDefinition } from '../../canvas-elements/element-definition';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { DialogComponent } from './dialog.component';
import { dialogV2Migration } from './migrations/002-dialog.migration';
// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    dialog: DialogDef;
  }
}

export const DIALOG_HEADER_SLOT = 'header';
export const DIALOG_CONTENT_SLOT = 'content';
export const DIALOG_FOOTER_SLOT = 'footer';
export const DIALOG_SLOTS = [DIALOG_HEADER_SLOT, DIALOG_CONTENT_SLOT, DIALOG_FOOTER_SLOT] as const;
export const DIALOG_COLUMN_WIDTH = 375;
export const DIALOG_COLUMN_HEIGHT = 325;

const MIN_SIZE = 150;
const MAX_WIDTH = 1280;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full', 'min'];

export type DialogSlots =
  | typeof DIALOG_HEADER_SLOT
  | typeof DIALOG_CONTENT_SLOT
  | typeof DIALOG_FOOTER_SLOT;

export type DialogDef = WithSlots<DialogSlots> & WithProps<DialogPropsModel>;
export type DialogElement = CanvasElementFrom<'dialog'>;

export function getDialogDefinition(): CanvasElementDefinition<'dialog'> {
  return {
    type: 'dialog',
    get componentType(): typeof DialogComponent {
      return DialogComponent;
    },
    isListed: false,
    name: 'Dialog',
    width: { mode: 'value', fixed: true, value: DIALOG_COLUMN_WIDTH },
    height: { mode: 'value', fixed: false, value: DIALOG_COLUMN_HEIGHT },
    propsConfig: getPropsConfig(),
    defaults: getDefaults(),
    slots: {
      ids: [DIALOG_HEADER_SLOT, DIALOG_CONTENT_SLOT, DIALOG_FOOTER_SLOT],
      [DIALOG_HEADER_SLOT]: {
        children: [],
        alignment: {
          horizontal: 'left',
          vertical: 'middle',
          syncVerticalLineAlignment: true,
        },
        props: {
          style: {
            padding: '16px 24px 16px 24px',
          },
        },
      },
      [DIALOG_CONTENT_SLOT]: {
        children: [],
        props: {
          style: {
            padding: '0px 0px 0px 0px',
          },
        },
      },
      [DIALOG_FOOTER_SLOT]: {
        children: [],
        props: {
          style: {
            padding: '12px 24px 12px 24px',
          },
        },
        alignment: {
          horizontal: 'right',
          vertical: 'middle',
          syncVerticalLineAlignment: true,
        },
      },
    },
    features: {
      resizeWithChild: true,
      hideEmptySlots: true,
    },

    version: 2,
    migrations: {
      2: dialogV2Migration,
    },
  };
}

registerElementDefinition(getDialogDefinition());

export type DialogPropsModel = {
  content: {
    options: {
      hideCloseButton: boolean;
    };
  };
  style: {
    container: PropertiesConfigInputBackground & {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
  };
};

function getDefaults(): PropsOf<'dialog'> {
  return {
    content: {
      options: { hideCloseButton: false },
    },
    style: {
      container: {
        offset: { padding: '0px' },
        outlineWidth: '1px',
        fill: { color1: '$t.canvas', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
        visualEffect: {
          type: 'drop-shadow',
          data: { x: '0', y: '4', blur: '24', color1: '#00000014' },
        },
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('dialog', [
    epc.section('content', 'Content', [
      epc.subsections.subsection('content', 'Type', [
        epc.fields.radiosField(
          '$$page.type',
          'Type',
          [
            { label: 'Dialog', value: 'Dialog' },
            { label: 'Drawer', value: 'Drawer' },
          ],
          { hideLabel: true, renderType: 'text', stretched: true },
        ),
      ]),
      epc.subsections.subsection('content', 'Options', [
        epc.fields.toggleField('content.options.hideCloseButton', 'Show top-right close button', {
          isNegated: true,
        }),
      ]),
      epc.subsections.subsection('content', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
      ]),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.subsection('style.container', 'Padding and Alignment', [
          epc.fields.spacingAndAlignContainerField('style.container.offset', {
            hasPadding: true,
            hasAlign: true,
          }),
        ]),
        epc.subsections.elementBackgroundSubsection('style.container', 'Appearance', {
          allowNoneFill: false,
          allowNoneOutline: false,
          fillLabel: 'Background',
        }),
      ],
      true,
    ),
  ]);
}
